"use client";

import { useState, useEffect } from "react";
import { Drawer, Button, Spin, message } from "antd";
import { ChevronLeft, ChevronRight, MessageSquare, Phone, Video, User } from "lucide-react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { getAvailableSlots } from "../../redux/api/Appoinment/GetAvailableSlotSlice";
import { createCounsellorAppointment } from "../../redux/api/Appoinment/CreateAppointmentSlice";
import {
  resetCreateAppointment,
} from "../../redux/slice/Appointment";

export default function FollowUpDialog({ open, onClose, clientId }) {
  const dispatch = useDispatch();
  const { data: availableSlotsData, loading: slotsLoading } = useSelector(
    (state) => state.GET_AVAILABLE_SLOT
  );
  const { data: createAppointmentData, loading: createLoading, error: createError } = useSelector(
    (state) => state.CREATE_APPOINTMENT
  );

  const [selectedMode, setSelectedMode] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState(null);
  const [reason, setReason] = useState("");
  const [currentDate, setCurrentDate] = useState(new Date());
  const [dateOffset, setDateOffset] = useState(0);
  const [availableSlots, setAvailableSlots] = useState({});

  const modes = [
    { id: "CHAT", label: "Chat", icon: MessageSquare },
    { id: "AUDIO", label: "Phone Call", icon: Phone },
    { id: "VIDEO", label: "Video Call", icon: Video },
    { id: "IN_PERSON", label: "In-Person", icon: User },
  ];

  // Fetch available slots when dialog opens or when current month changes
  useEffect(() => {
    if (open) {
      fetchAvailableSlots();
    }
  }, [open, currentDate]);

  // Update available slots when data is fetched
  useEffect(() => {
    if (availableSlotsData) {
      setAvailableSlots(availableSlotsData);
    }
  }, [availableSlotsData]);

  // Reset selected time when mode or date changes
  useEffect(() => {
    setSelectedTime(null);
  }, [selectedMode, selectedDate]);

  const fetchAvailableSlots = () => {
    const counsellorId = localStorage.getItem("cid");
    if (!counsellorId) return;

    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() &&
                           currentDate.getFullYear() === today.getFullYear();

    let startDate, endDate;

    if (isCurrentMonth) {
      // For current month, start from today
      startDate = formatDateForAPI(today);
      // End date is last day of current month
      endDate = formatDateForAPI(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    } else {
      // For next month, start from first day
      startDate = formatDateForAPI(new Date(currentDate.getFullYear(), currentDate.getMonth(), 1));
      // End date is last day of the month
      endDate = formatDateForAPI(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0));
    }

    dispatch(getAvailableSlots(counsellorId, startDate, endDate));
  };

  const formatDateForAPI = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  // Generate dates for the current month view
  const generateDates = () => {
    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() &&
                           currentDate.getFullYear() === today.getFullYear();

    const dates = [];
    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

    if (isCurrentMonth) {
      // For current month, show 6 days starting from today + dateOffset
      for (let i = 0; i < 6; i++) {
        const date = new Date(today);
        date.setDate(today.getDate() + dateOffset + i);

        dates.push({
          day: date.getDate(),
          label: (dateOffset === 0 && i === 0) ? "Today" : dayNames[date.getDay()],
          fullDate: date,
          month: date.getMonth(),
          year: date.getFullYear()
        });
      }

      // Set first date as default selected if no date selected
      if (selectedDate === null) {
        setSelectedDate(dates[0].day);
      }
    } else {
      // For future months, show 6 days starting from dateOffset
      for (let i = 1; i <= 6; i++) {
        const date = new Date(currentDate.getFullYear(), currentDate.getMonth(), i + dateOffset);

        dates.push({
          day: date.getDate(),
          label: dayNames[date.getDay()],
          fullDate: date,
          month: date.getMonth(),
          year: date.getFullYear()
        });
      }
    }

    return dates;
  };

  const dates = generateDates();

  // Get current month name
  const getMonthName = () => {
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`;
  };

  // Navigate months
  const goToPreviousMonth = () => {
    const today = new Date();
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() - 1);

    // Don't allow going to past months
    if (newDate.getFullYear() >= today.getFullYear() &&
        newDate.getMonth() >= today.getMonth()) {
      setCurrentDate(newDate);
      setSelectedDate(null); // Reset selection when changing months
      setDateOffset(0); // Reset date offset
    }
  };

  const goToNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + 1);
    setCurrentDate(newDate);
    setSelectedDate(null); // Reset selection when changing months
    setDateOffset(0); // Reset date offset
  };

  // Navigate dates
  const goToPreviousDates = () => {
    const today = new Date();
    const isCurrentMonth = currentDate.getMonth() === today.getMonth() &&
                           currentDate.getFullYear() === today.getFullYear();

    if (isCurrentMonth && dateOffset > 0) {
      setDateOffset(dateOffset - 6);
      setSelectedDate(null);
    } else if (isCurrentMonth && dateOffset === 0) {
      // If no left navigation available for dates, reset to current date
      setDateOffset(0);
      setSelectedDate(null);
    } else if (!isCurrentMonth && dateOffset > 0) {
      setDateOffset(dateOffset - 6);
      setSelectedDate(null);
    }
  };

  const goToNextDates = () => {
    setDateOffset(dateOffset + 6);
    setSelectedDate(null);
  };

  // Get time slots for selected date
  const getTimeSlotsForDate = () => {
    if (!selectedDate) return [];

    const selectedDateObj = dates.find(d => d.day === selectedDate);
    if (!selectedDateObj) return [];

    const dateKey = formatDateForAPI(selectedDateObj.fullDate);
    const slotsForDate = availableSlots[dateKey] || [];

    // Filter slots based on selected mode and availability
    const filteredSlots = slotsForDate.filter(slot => {
      const modes = slot.modes.split(',').map(m => m.trim());
      return modes.includes(selectedMode) && slot.available;
    });

    // For current date, filter out past time slots
    const now = new Date();
    const isToday = selectedDateObj.fullDate.toDateString() === now.toDateString();

    if (isToday) {
      const currentTime = now.getHours() * 60 + now.getMinutes();
      return filteredSlots.filter(slot => {
        const [hours, minutes] = slot.startTime.split(':').map(Number);
        const slotTime = hours * 60 + minutes;
        return slotTime > currentTime;
      });
    }

    return filteredSlots;
  };

  const timeSlots = getTimeSlotsForDate();

  // Convert 24-hour time to 12-hour format
  const formatTime = (time) => {
    const [hours, minutes] = time.split(':');
    const hour = parseInt(hours);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const hour12 = hour % 12 || 12;
    return `${hour12}:${minutes} ${ampm}`;
  };

  const handleConfirm = async () => {
    // Validate required fields
    if (!selectedMode) {
      message.error("Please select a mode for the appointment");
      return;
    }

    if (!selectedDate || !selectedTime) {
      message.error("Please select both date and time for the appointment");
      return;
    }

    // Get the selected date object
    const selectedDateObj = dates.find(d => d.day === selectedDate);
    if (!selectedDateObj) {
      message.error("Invalid date selection");
      return;
    }

    // Get the selected slot
    const selectedSlot = timeSlots.find(slot => slot.startTime === selectedTime);
    if (!selectedSlot) {
      message.error("Invalid time slot selection");
      return;
    }

    // Get clientId from localStorage or prop
    const cid = clientId || localStorage.getItem("cid");
    if (!cid) {
      message.error("Client ID not found");
      return;
    }

    // Prepare appointment data
    const appointmentData = {
      clientId: cid,
      timeSlotRequest: {
        id: selectedSlot.id,
        startTime: selectedSlot.startTime,
        endTime: selectedSlot.endTime,
        appointmentDate: formatDateForAPI(selectedDateObj.fullDate),
      },
      mode: selectedMode === "IN_PERSON" ? "INPERSON" : selectedMode,
      anonymous: false,
      deviceIp: null,
    };

    try {
      const result = await dispatch(createCounsellorAppointment(appointmentData));

      if (result) {
        message.success("Follow-up appointment created successfully");
        handleCancel(); // Close and reset the form
      }
    } catch (error) {
      message.error(error.message || "Failed to create appointment");
    }
  };

  const handleCancel = () => {
    // Reset form state
    setSelectedMode(null);
    setSelectedDate(null);
    setSelectedTime(null);
    setReason("");
    setCurrentDate(new Date());
    setDateOffset(0);
    dispatch(resetCreateAppointment());
    onClose();
  };

  return (
    <Drawer
      title="Create a Follow-up Appointment"
      placement="right"
      footer={
        <div className="flex justify-end gap-3">
          <button
            className="font-poppins text-[14px] text-[#4B61FF] bg-[#ffffff] px-4 py-2 rounded-[8px]"
            style={{ border: "1px solid #4B61FF" }}
            onClick={handleCancel}
          >
            Cancel
          </button>
          <button
            className="font-poppins text-[14px] text-[#FFFFFF] btn-gradient px-4 py-2 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleConfirm}
            disabled={createLoading || !selectedMode || !selectedDate || !selectedTime}
          >
            {createLoading ? "Creating..." : "Confirm"}
          </button>
        </div>
      }
      open={open}
      onClose={handleCancel}
      className="text-[24px] font-poppins text-[#171717]"
      width={450}
    >
      <div className="flex flex-col gap-6">
        {/* Select Mode */}
        <div>
          <span className="text-[16px] text-[#171717] font-poppins font-semibold">
            Select Mode
          </span>
          <div className="grid grid-cols-4 gap-3 mt-4">
            {modes.map((mode) => {
              const IconComponent = mode.icon;
              const isSelected = selectedMode === mode.id;

              return (
                <button
                  key={mode.id}
                  onClick={() => setSelectedMode(mode.id)}
                  className={`flex flex-col items-center p-3 rounded-xl transition-all ${
                    isSelected
                      ? "bg-[#4B61FF] text-white"
                      : "bg-[#EFF6FF] hover:bg-[#F1F5F9]"
                  }`}
                >
                  <div
                    className={`p-3 rounded-full mb-2 ${
                      isSelected
                        ? "bg-[#6EE7B7]"
                        : "bg-[#F5F9FF]"
                    }`}
                  >
                    <IconComponent
                      className={`h-5 w-5 ${
                        isSelected
                          ? "text-[#4B61FF]"
                          : "text-[#4B61FF]"
                      }`}
                    />
                  </div>
                  <span
                    className={`text-sm font-normal font-poppins ${
                      isSelected
                        ? "text-white"
                        : "text-[#64748B]"
                    }`}
                  >
                    {mode.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="w-full h-[0.5px] bg-gray-200"></div>

        {/* Select Date */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[16px] text-[#171717] font-poppins font-semibold">
              Select Date
            </span>
            <div className="flex items-center gap-2">
              <button
                onClick={goToPreviousMonth}
                className="p-1 hover:bg-[#EFF6FF] rounded transition-colors duration-200 bg-transparent border-none"
                style={{ background: 'transparent', border: 'none', padding: '4px' }}
                disabled={currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear()}
              >
                <ChevronLeft className={`h-5 w-5 ${
                  currentDate.getMonth() === new Date().getMonth() && currentDate.getFullYear() === new Date().getFullYear()
                    ? 'text-[#94A3B8]'
                    : 'text-[#171717] hover:text-[#4B61FF]'
                }`} />
              </button>
              <span className="text-base text-[#171717] font-poppins min-w-[120px] text-center">
                {getMonthName()}
              </span>
              <button
                onClick={goToNextMonth}
                className="p-1 hover:bg-[#EFF6FF] rounded transition-colors duration-200 bg-transparent border-none"
                style={{ background: 'transparent', border: 'none', padding: '4px' }}
              >
                <ChevronRight className="h-5 w-5 text-[#171717] hover:text-[#4B61FF]" />
              </button>
            </div>
          </div>

          <div className="flex items-center justify-between mt-4 mb-2">
            <button
              onClick={goToPreviousDates}
              className="p-1 hover:bg-[#EFF6FF] rounded transition-colors duration-200 bg-transparent border-none"
              style={{ background: 'transparent', border: 'none', padding: '4px' }}
              disabled={currentDate.getMonth() === new Date().getMonth() &&
                       currentDate.getFullYear() === new Date().getFullYear() &&
                       dateOffset === 0}
            >
              <ChevronLeft className={`h-4 w-4 ${
                currentDate.getMonth() === new Date().getMonth() &&
                currentDate.getFullYear() === new Date().getFullYear() &&
                dateOffset === 0
                  ? 'text-[#94A3B8]'
                  : 'text-[#171717] hover:text-[#4B61FF]'
              }`} />
            </button>
            <button
              onClick={goToNextDates}
              className="p-1 hover:bg-[#EFF6FF] rounded transition-colors duration-200 bg-transparent border-none"
              style={{ background: 'transparent', border: 'none', padding: '4px' }}
            >
              <ChevronRight className="h-4 w-4 text-[#171717] hover:text-[#4B61FF]" />
            </button>
          </div>

          <div className="grid grid-cols-6 gap-2">
            {dates.map((date) => {
              const isSelected = selectedDate === date.day;

              return (
                <button
                  key={date.day}
                  onClick={() => setSelectedDate(date.day)}
                  className={`flex flex-col items-center p-2 rounded-xl transition-all ${
                    isSelected
                      ? "bg-[#4B61FF] text-white"
                      : "bg-[#EFF6FF] hover:bg-[#F1F5F9]"
                  }`}
                >
                  <div
                    className={`flex items-center justify-center w-8 h-8 rounded-full mb-1 ${
                      isSelected
                        ? "bg-[#6EE7B7] text-[#4B61FF]"
                        : "bg-[#F5F9FF] text-[#4B61FF]"
                    }`}
                  >
                    <span className="text-sm font-normal font-poppins">
                      {date.day}
                    </span>
                  </div>
                  <span
                    className={`text-xs font-normal font-poppins ${
                      isSelected
                        ? "text-white"
                        : "text-[#64748B]"
                    }`}
                  >
                    {date.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        <div className="w-full h-[0.5px] bg-gray-200"></div>

        {/* Select Time */}
        <div>
          <div className="flex items-center justify-between">
            <span className="text-[16px] text-[#171717] font-poppins font-semibold">
              Select Time
            </span>
            <span className="text-[14px] text-[#171717] font-poppins">
              {timeSlots.length} Slots
            </span>
          </div>

          {slotsLoading ? (
            <div className="flex justify-center items-center mt-4 py-8">
              <Spin size="large" />
            </div>
          ) : timeSlots.length === 0 ? (
            <div className="text-center text-[#64748B] font-poppins mt-4 py-8">
              {selectedDate ? "No available slots for this date and mode" : "Please select a date"}
            </div>
          ) : (
            <div className="grid grid-cols-4 gap-2 mt-4">
              {timeSlots.map((slot) => {
                const isSelected = selectedTime === slot.startTime;

                return (
                  <Button
                    key={slot.startTime}
                    onClick={() => setSelectedTime(slot.startTime)}
                    className={`text-[14px] border-0 font-poppins h-[40px] ${
                      isSelected
                        ? "bg-[#4B61FF] text-[#FFFFFF]"
                        : "bg-[#EFF6FF] text-[#334155]"
                    }`}
                  >
                    {formatTime(slot.startTime)}
                  </Button>
                );
              })}
            </div>
          )}
        </div>

        <div className="w-full h-[0.5px] bg-gray-200"></div>

        {/* Reason */}
        <div>
          <div className="mb-2">
            <span className="text-[14px] text-[#171717] font-poppins font-medium">
              Reason{" "}
              <span className="text-[12px] text-[#334155] font-normal">
                (Optional)
              </span>
            </span>
          </div>
          <textarea
            className="w-full h-[120px] p-4 bg-[#F8FAFC] border border-[#CBD5E1] rounded-xl font-poppins text-[16px] text-[#64748B] placeholder-[#64748B] resize-none focus:outline-none focus:ring-2 focus:ring-[#B3D7FF]"
            placeholder="Type here"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
        </div>
      </div>
    </Drawer>
  );
}

FollowUpDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clientId: PropTypes.string,
};